import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import Autocomplete from './Autocomplete';
import { RenderValueTypes } from './select-consts';
import * as S from './styled';

const DELAY_MS_DEBOUNCE_SEARCH = 1000;
const STRING_MIN_LENGTH = 3;

const LOCAL_STRINGS = {
  noOptions: 'No options',
  searching: 'Searching...',
  min_search_text_length: 'Enter at least'
};

const AsyncAutocomplete = ({
  testId,
  value,
  onChange,
  fetchOptionsData,
  label,
  placeholder,
  width,
  height,
  renderValue,
  chipsCount,
  multiple,
  minLength = STRING_MIN_LENGTH,
  delay = DELAY_MS_DEBOUNCE_SEARCH,
  disabled,
  disablePortal,
  error
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const buildNoOptionText = useMemo(() => {
    if (loading) {
      // API was sent and waiting for a response
      return (
        <S.Searching>
          {LOCAL_STRINGS.searching}
          <S.CircularProgress className="loading" size={15} />
        </S.Searching>
      );
    }
    if (inputValue?.length < minLength) {
      return `${LOCAL_STRINGS.min_search_text_length} ${minLength} characters`;
    }
    if (inputValue?.length >= minLength) {
      return LOCAL_STRINGS.noOptions;
    }
  }, [inputValue, loading]);

  // Debounced search function
  const debouncedFetch = useCallback(
    debounce(async val => {
      setLoading(true);
      try {
        const result = await fetchOptionsData(val);
        setOptions(result);
      } catch {
        setOptions([]);
      }
      setLoading(false);
    }, delay),
    [fetchOptionsData]
  );

  useEffect(() => {
    if (!inputValue || inputValue.length < minLength) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncedFetch(inputValue);
  }, [inputValue]);

  const onInputChangeHandler = (_, newInputValue, reason) => {
    if (reason !== 'reset') {
      setInputValue(newInputValue);
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      testId={testId}
      width={width}
      height={height}
      label={label}
      disabled={disabled}
      placeholder={placeholder}
      multiple={multiple}
      options={options}
      value={value}
      onInputChange={onInputChangeHandler}
      onChange={onChange}
      noOptionsText={buildNoOptionText}
      loading={loading}
      chipsCount={chipsCount}
      renderValue={renderValue}
      disablePortal={disablePortal}
      error={error}
    />
  );
};

AsyncAutocomplete.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  renderValue: PropTypes.oneOf([
    RenderValueTypes.CHIPS,
    RenderValueTypes.COMMA_SEPARATED,
    RenderValueTypes.COUNT,
    PropTypes.func
  ]),
  chipsCount: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  fetchOptionsData: PropTypes.func.isRequired, // should return array of {label, value}
  testId: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  width: PropTypes.bool,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  minLength: PropTypes.number,
  delay: PropTypes.number,
  error: PropTypes.bool
};

export default AsyncAutocomplete;
